import { useState } from 'react';
import { ActionIcon, Alert, Button, Group, NumberInput, Select, Stack, Table, Text } from '@mantine/core';
import { IconAlertTriangle, IconPlus, IconTrash } from '@tabler/icons-react';
import type { PackSpecPackagingLine, UpsertPackSpecInput } from '@zebra/core';
import { ApiRequestError } from '../../../api/client';
import { parseNum } from '../../../lib/parseNum';
import { isPackSpecMissing, useActiveProducts, useAllProducts, usePackSpec, useUpsertPackSpec } from '../api';
import { formatLineQty, formatQty, qtyInputLabel } from '../format';

interface PackagingRow {
  key: string;
  productId: string;
  qty: number | string;
}

/** Pack spec view/configure for one pack (SKU) product (BUILD_SPEC.md §5.2, §8.5).
 * Mirrors features/silo-counts/components/CalibrationForm.tsx's identical "view
 * summary, or configure inline via the existing nested-resource endpoint" pattern
 * — this feature consumes + writes `PUT /config/products/:id/pack-spec` (owned by
 * config module, G13) rather than building a general products/pack-specs settings
 * screen (out of this goal's scope, see api.ts's header). */
export function PackSpecPanel({ packProductId, onSaved }: { packProductId: string; onSaved?: () => void }) {
  const specQuery = usePackSpec(packProductId);
  const missing = isPackSpecMissing(specQuery.error);
  // `products` (active only) feeds the bulk-product PICKER; `allProducts` feeds
  // every LOOKUP (view-mode summary, packaging names, edit-mode unit label) so a
  // spec whose bulk/packaging product was later deactivated still resolves its
  // name/baseUom — see api.ts's useAllProducts doc.
  const productsQuery = useActiveProducts();
  const allProductsQuery = useAllProducts();
  const upsert = useUpsertPackSpec(packProductId);
  const products = productsQuery.data?.items ?? [];
  const allProducts = allProductsQuery.data?.items ?? [];

  const [editing, setEditing] = useState(false);
  const [bulkProductId, setBulkProductId] = useState<string | null>(null);
  const [qtyPerPack, setQtyPerPack] = useState<number | string>('');
  const [packaging, setPackaging] = useState<PackagingRow[]>([]);
  const parsedQtyPerPack = parseNum(qtyPerPack);

  function startEdit() {
    setBulkProductId(specQuery.data?.bulkProductId ?? null);
    setQtyPerPack(specQuery.data ? Number(specQuery.data.qtyPerPack) : '');
    setPackaging((specQuery.data?.packaging ?? []).map((p) => ({ key: crypto.randomUUID(), productId: p.productId, qty: Number(p.qty) })));
    upsert.reset();
    setEditing(true);
  }

  function handleSave() {
    if (!bulkProductId || parsedQtyPerPack === null) return;
    const lines: PackSpecPackagingLine[] = packaging
      .filter((r) => r.productId && parseNum(r.qty) !== null)
      .map((r) => ({ productId: r.productId, qty: (parseNum(r.qty) as number).toFixed(3) }));
    const body: UpsertPackSpecInput = {
      bulkProductId,
      qtyPerPack: parsedQtyPerPack.toFixed(3),
      packaging: lines.length > 0 ? lines : null,
    };
    upsert.mutate(body, {
      onSuccess: () => {
        setEditing(false);
        onSaved?.();
      },
    });
  }

  if (specQuery.isLoading) {
    return (
      <Text c="dimmed" size="sm">
        Loading pack spec…
      </Text>
    );
  }

  if (!editing && specQuery.data) {
    const spec = specQuery.data;
    const bulkProduct = allProducts.find((p) => p.id === spec.bulkProductId);
    return (
      <Stack gap="xs">
        <Text size="sm">
          {/* Unit-aware: a pack spec's bulk product is not always mass-tracked —
              e.g. "Firelighters box of 48" contains 48 x firelighter EACH units,
              not 48 kg (BUILD_SPEC.md §5.2 pack_specs example). */}
          <strong>{formatLineQty(spec.qtyPerPack, bulkProduct?.baseUom)}</strong> of {bulkProduct?.name ?? spec.bulkProductId} per pack
          {spec.packaging && spec.packaging.length > 0 && (
            <>
              {' '}
              + {spec.packaging.map((p) => `${formatQty(p.qty)} × ${allProducts.find((pr) => pr.id === p.productId)?.name ?? p.productId}`).join(', ')}
            </>
          )}
        </Text>
        <Group>
          <Button size="xs" variant="light" onClick={startEdit}>
            Edit pack spec
          </Button>
        </Group>
      </Stack>
    );
  }

  if (!editing && missing) {
    return (
      <Stack gap="xs">
        <Alert color="amber" icon={<IconAlertTriangle size={18} />}>
          No pack spec configured for this product yet.
        </Alert>
        <Group>
          <Button size="xs" onClick={startEdit}>
            Configure pack spec
          </Button>
        </Group>
      </Stack>
    );
  }

  if (!editing && specQuery.isError) {
    return (
      <Alert color="red" icon={<IconAlertTriangle size={18} />}>
        {specQuery.error instanceof ApiRequestError ? specQuery.error.message : 'Could not load pack spec.'}
      </Alert>
    );
  }

  return (
    <Stack gap="sm">
      <Select
        label="Bulk product consumed per pack"
        placeholder="Bulk product"
        data={products.map((p) => ({ value: p.id, label: `${p.sku} — ${p.name}` }))}
        value={bulkProductId}
        onChange={setBulkProductId}
        searchable
      />
      {/* Label lookup uses allProducts: when EDITING an existing spec,
          `bulkProductId` starts as the saved value, which may reference a
          since-deactivated product the active-only picker list no longer
          contains. */}
      <NumberInput
        label={qtyInputLabel(allProducts.find((p) => p.id === bulkProductId)?.baseUom, 'Qty per pack (kg)', 'Qty per pack (units)')}
        decimalScale={3}
        min={0}
        value={qtyPerPack}
        onChange={setQtyPerPack}
      />
      <Text size="sm" fw={500}>
        Packaging consumables per pack
      </Text>
      <Table.ScrollContainer minWidth={640}>
        <Table>
          <Table.Tbody>
            {packaging.map((row) => (
              <Table.Tr key={row.key}>
                <Table.Td>
                  <Select
                    placeholder="Product"
                    data={products.map((p) => ({ value: p.id, label: `${p.sku} — ${p.name}` }))}
                    value={row.productId}
                    onChange={(v) => setPackaging((rows) => rows.map((r) => (r.key === row.key ? { ...r, productId: v ?? '' } : r)))}
                    searchable
                  />
                </Table.Td>
                <Table.Td w={140}>
                  <NumberInput
                    decimalScale={3}
                    min={0}
                    value={row.qty}
                    onChange={(v) => setPackaging((rows) => rows.map((r) => (r.key === row.key ? { ...r, qty: v } : r)))}
                  />
                </Table.Td>
                <Table.Td w={48}>
                  <ActionIcon color="red" variant="subtle" onClick={() => setPackaging((rows) => rows.filter((r) => r.key !== row.key))}>
                    <IconTrash size={16} />
                  </ActionIcon>
                </Table.Td>
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
      </Table.ScrollContainer>
      <Group>
        <Button
          size="xs"
          variant="default"
          leftSection={<IconPlus size={14} />}
          onClick={() => setPackaging((rows) => [...rows, { key: crypto.randomUUID(), productId: '', qty: '' }])}
        >
          Add packaging line
        </Button>
      </Group>
      {upsert.isError && (
        <Alert color="red" icon={<IconAlertTriangle size={18} />}>
          {upsert.error instanceof ApiRequestError ? upsert.error.message : 'Failed to save pack spec'}
        </Alert>
      )}
      <Group justify="flex-end">
        <Button variant="default" onClick={() => setEditing(false)}>
          Cancel
        </Button>
        <Button loading={upsert.isPending} disabled={!bulkProductId || parsedQtyPerPack === null} onClick={handleSave}>
          Save pack spec
        </Button>
      </Group>
    </Stack>
  );
}
